import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	Checkbox,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	Skeleton,
	CircularProgress,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { FiChevronsDown, FiChevronsUp, FiSearch } from "react-icons/fi";
import { MdOutlineClear } from "react-icons/md";
import { useTranslation } from "react-i18next";
import ModelIcon from "../ModelIcon/ModelIcon";
import ModelPDFIcon from "../ModelPDFIcon/ModelPDFIcon";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";

const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderHighlightedText = (text, term, color) => {
	if (!term || typeof text !== "string") {
		return text;
	}

	const regex = new RegExp(escapeRegExp(term), "ig");
	let lastIndex = 0;
	const parts = [];
	let match;

	while ((match = regex.exec(text)) !== null) {
		if (match.index > lastIndex) {
			parts.push(text.slice(lastIndex, match.index));
		}
		parts.push(
			<span key={`${match.index}-${match[0]}`} style={{ color }}>
				{match[0]}
			</span>
		);
		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < text.length) {
		parts.push(text.slice(lastIndex));
	}

	return parts.length > 0 ? parts : text;
};

const BomViewer = ({
	title,
	helperText,
	clearSelectionLabel,
	noDataLabel,
	machineName,
	machineNumber,
	machineNodeId,
	treeData = [],
	selectedNodeId = null,
	expandedNodes = [],
	searchTerm = "",
	bomSearchPlaceholder = "Search BOM",
	onNodeSelect,
	onClearSelection,
	onToggle,
	onProductClick,
	filtersActive = false,
	highlightedProductIds,
	treeItemRefs,
	checkedProductIds = [],
	onSelectionChange,
	machineProductIds = [],
	expandAllLabel = "Expand All",
	collapseAllLabel = "Collapse All",
	maxHeight = 360,
	variant = "inline",
	onNavigateToProduct,
	onOpenModal,
	modalButtonLabel = "",
	showCheckboxes = true,
	isLoading = false,
	modelAvailability = new Map(),
	onModelIconClick,
	modalOpening = false,
	machineHasDocumentation = false,
	onMachineDocumentationClick,
}) => {
	const { t } = useTranslation();
	
	// Ensure labels are translated - use prop if provided, otherwise translate default
	const finalExpandAllLabel = (expandAllLabel && expandAllLabel !== "Expand All") ? expandAllLabel : t('UI_TEXT.EXPAND_ALL');
	const finalCollapseAllLabel = (collapseAllLabel && collapseAllLabel !== "Collapse All") ? collapseAllLabel : t('UI_TEXT.COLLAPSE_ALL');
	
	const theme = useTheme();
	const internalTreeItemRefs = useRef({});
	const resolvedTreeItemRefs = treeItemRefs || internalTreeItemRefs;
	const listRef = useRef(null);
	const machineRowRef = useRef(null);
	const expandedLookup = useMemo(() => new Set(expandedNodes || []), [expandedNodes]);
	const highlightSet = highlightedProductIds || new Set();
	const normalizedTreeData = treeData || [];
	const checkedLookup = useMemo(
		() => new Set(checkedProductIds || []),
		[checkedProductIds]
	);
	const machineProducts = machineProductIds || [];
	const modelAvailabilityMap = useMemo(() => {
		if (modelAvailability instanceof Map) return modelAvailability;
		if (modelAvailability && typeof modelAvailability === "object") {
			return new Map(
				Object.entries(modelAvailability).map(([key, value]) => [
					String(key),
					value || {},
				])
			);
		}
		return new Map();
	}, [modelAvailability]);

	const getModelInfoForNode = useCallback(
		(node = {}) => {
			if (!node) return null;
			const tryKeys = [];
			if (node.productNumber) {
				const normalized = String(node.productNumber).trim().toUpperCase();
				if (normalized) {
					tryKeys.push(`pn:${normalized}`);
				}
			}
			if (node.productId) {
				const normalized = String(node.productId).trim().toUpperCase();
				if (normalized) {
					tryKeys.push(`id:${normalized}`);
				}
			}
			for (const key of tryKeys) {
				if (modelAvailabilityMap.has(key)) {
					return modelAvailabilityMap.get(key);
				}
			}
			return null;
		},
		[modelAvailabilityMap]
	);

	const handleModelIconButtonClick = useCallback(
		(event, node, type) => {
			if (event) {
				event.stopPropagation();
			}
			if (typeof onModelIconClick !== "function") return;
			const modelInfo = getModelInfoForNode(node);
			if (!modelInfo) return;
			onModelIconClick({ node, modelInfo, type });
		},
		[getModelInfoForNode, onModelIconClick]
	);
	const isModalVariant = variant === "modal";
	const [bomSearchTerm, setBomSearchTerm] = useState("");
	const [bomSearchMatches, setBomSearchMatches] = useState([]);
	const [activeMatchIndex, setActiveMatchIndex] = useState(-1);
	const bomSearchMatchSet = useMemo(
		() => new Set(bomSearchMatches),
		[bomSearchMatches]
	);
	const activeMatchNodeId =
		activeMatchIndex >= 0 && bomSearchMatches[activeMatchIndex]
			? bomSearchMatches[activeMatchIndex]
			: null;
	const highlightTerm = bomSearchTerm || searchTerm;
	const matchCount = bomSearchMatches.length;
	const matchDisplay =
		matchCount > 0 && activeMatchIndex >= 0
			? `${activeMatchIndex + 1} / ${matchCount}`
			: matchCount > 0
				? `0 / ${matchCount}`
				: "0 / 0";

	useEffect(() => {
		if (!treeItemRefs) {
			internalTreeItemRefs.current = {};
		}
	}, [normalizedTreeData, treeItemRefs]);

	const allNodeIds = useMemo(() => {
		const ids = [];
		const traverse = (nodes = []) => {
			nodes.forEach((node) => {
				ids.push(node.nodeId);
				if (node.children && node.children.length > 0) {
					traverse(node.children);
				}
			});
		};
		traverse(normalizedTreeData);
		return ids;
	}, [normalizedTreeData]);

	const { flatTreeNodes, parentLookup } = useMemo(() => {
		const flatNodes = [];
		const parents = new Map();
		const traverse = (nodes = [], parentId) => {
			nodes.forEach((node) => {
				flatNodes.push(node);
				parents.set(node.nodeId, parentId || machineNodeId);
				if (node.children && node.children.length > 0) {
					traverse(node.children, node.nodeId);
				}
			});
		};
		traverse(normalizedTreeData, machineNodeId);
		return { flatTreeNodes: flatNodes, parentLookup: parents };
	}, [normalizedTreeData, machineNodeId]);

	const visibleTreeRows = useMemo(() => {
		const rows = [];
		const rootExpanded = expandedLookup.has(machineNodeId);
		if (!rootExpanded) {
			return rows;
		}
		const traverse = (nodes = [], depth = 0) => {
			nodes.forEach((node) => {
				if (!node) return;
				rows.push({ type: "node", node, nodeId: node.nodeId, depth });
				const hasChildren = Array.isArray(node.children) && node.children.length > 0;
				if (hasChildren && expandedLookup.has(node.nodeId)) {
					traverse(node.children, depth + 1);
				}
			});
		};
		traverse(normalizedTreeData, 0);
		return rows;
	}, [expandedLookup, machineNodeId, normalizedTreeData]);

	const visibleIndexLookup = useMemo(() => {
		const map = new Map();
		visibleTreeRows.forEach((row, index) => {
			if (row?.nodeId) {
				map.set(row.nodeId, index);
			}
		});
		return map;
	}, [visibleTreeRows]);

	const expandAncestors = useCallback(
		(targetNodeId) => {
			if (!targetNodeId || !onToggle) return;
			const next = new Set(expandedNodes || []);
			let changed = false;
			let parentId = parentLookup.get(targetNodeId);
			while (parentId) {
				if (!next.has(parentId)) {
					next.add(parentId);
					changed = true;
				}
				parentId = parentLookup.get(parentId);
			}
			if (machineNodeId && !next.has(machineNodeId)) {
				next.add(machineNodeId);
				changed = true;
			}
			if (changed) {
				onToggle(Array.from(next));
			}
		},
		[expandedNodes, machineNodeId, onToggle, parentLookup]
	);

	const focusNode = useCallback(
		(nodeId) => {
			if (!nodeId) return;
			if (nodeId === machineNodeId) {
				if (machineRowRef.current?.scrollIntoView) {
					machineRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
				}
				return;
			}
			const index = visibleIndexLookup.get(nodeId);
			if (typeof index === "number" && listRef.current?.scrollToItem) {
				listRef.current.scrollToItem(index, "center");
				return;
			}
			const element = resolvedTreeItemRefs.current?.[nodeId];
			if (element && element.scrollIntoView) {
				element.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		},
		[listRef, machineNodeId, resolvedTreeItemRefs, visibleIndexLookup]
	);

	const focusNextMatch = useCallback(() => {
		if (bomSearchMatches.length === 0) return;
		const nextIndex =
			bomSearchMatches.length === 1
				? 0
				: (activeMatchIndex + 1) % bomSearchMatches.length;
		const nextNodeId = bomSearchMatches[nextIndex];
		setActiveMatchIndex(nextIndex);
		expandAncestors(nextNodeId);
		setTimeout(() => focusNode(nextNodeId), 80);
	}, [activeMatchIndex, bomSearchMatches, expandAncestors, focusNode]);

	const focusPreviousMatch = useCallback(() => {
		if (bomSearchMatches.length === 0) return;
		const prevIndex =
			bomSearchMatches.length === 1
				? 0
				: (activeMatchIndex - 1 + bomSearchMatches.length) % bomSearchMatches.length;
		const prevNodeId = bomSearchMatches[prevIndex];
		setActiveMatchIndex(prevIndex);
		expandAncestors(prevNodeId);
		setTimeout(() => focusNode(prevNodeId), 80);
	}, [activeMatchIndex, bomSearchMatches, expandAncestors, focusNode]);

	useEffect(() => {
		if (resolvedTreeItemRefs?.current) {
			resolvedTreeItemRefs.current.__scrollToNode = focusNode;
		}
	}, [focusNode, resolvedTreeItemRefs]);

	useEffect(() => {
		setBomSearchTerm("");
		setBomSearchMatches([]);
		setActiveMatchIndex(-1);
	}, [machineNodeId]);

	useEffect(() => {
		const normalizedTerm = bomSearchTerm.trim().toLowerCase();
		if (!normalizedTerm) {
			setBomSearchMatches([]);
			setActiveMatchIndex(-1);
			return;
		}
		const matches = flatTreeNodes
			.filter((node) => {
				const name = node.productName?.toLowerCase() || "";
				const number = node.productNumber?.toLowerCase() || "";
				return name.includes(normalizedTerm) || number.includes(normalizedTerm);
			})
			.map((node) => node.nodeId);
		setBomSearchMatches(matches);
		setActiveMatchIndex(matches.length ? 0 : -1);
		if (matches.length > 0) {
			expandAncestors(matches[0]);
		}
	}, [bomSearchTerm, expandAncestors, flatTreeNodes]);

	const handleBomSearchKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			if (event.shiftKey) {
				focusPreviousMatch();
			} else {
				focusNextMatch();
			}
		}
	};

	const handleBomSearchClear = () => {
		setBomSearchTerm("");
		setBomSearchMatches([]);
		setActiveMatchIndex(-1);
	};

	const registerNodeRef = (nodeId, element) => {
		if (!resolvedTreeItemRefs || !resolvedTreeItemRefs.current) return;
		if (element) {
			resolvedTreeItemRefs.current[nodeId] = element;
		} else {
			delete resolvedTreeItemRefs.current[nodeId];
		}
	};

	const handleToggle = (nodeId) => {
		if (!onToggle) return;
		const isExpanded = expandedLookup.has(nodeId);
		const nextExpanded = isExpanded
			? (expandedNodes || []).filter((id) => id !== nodeId)
			: [...(expandedNodes || []), nodeId];
		onToggle(nextExpanded);
	};

	const handleSelect = (nodeId, nodeData = null) => {
		if (onNodeSelect) {
			onNodeSelect(nodeId, nodeData);
		}
		if (nodeData?.productId && typeof onProductClick === "function") {
			onProductClick(nodeData);
		}
	};

	const getNodeProductIds = (node) => {
		if (node?.allProductIds && node.allProductIds.length > 0) {
			return node.allProductIds;
		}
		if (node?.productId) {
			return [node.productId];
		}
		return [];
	};

	const handleNodeCheckboxToggle = (node) => {
		if (!showCheckboxes || !onSelectionChange) return;
		const ids = getNodeProductIds(node);
		if (ids.length === 0) return;
		const isChecked = ids.every((id) => checkedLookup.has(id));
		const next = new Set(checkedLookup);
		if (isChecked) {
			ids.forEach((id) => next.delete(id));
		} else {
			ids.forEach((id) => next.add(id));
		}
		onSelectionChange(Array.from(next));
	};

	const handleMachineCheckboxToggle = () => {
		if (!showCheckboxes || !onSelectionChange || machineProducts.length === 0)
			return;
		const machineChecked = machineProducts.every((id) => checkedLookup.has(id));
		if (machineChecked) {
			onSelectionChange([]);
		} else {
			onSelectionChange(machineProducts);
		}
	};

	const handleExpandCollapse = (shouldExpand) => {
		if (!onToggle) return;
		if (shouldExpand) {
			const unique = new Set([machineNodeId, ...allNodeIds]);
			onToggle(Array.from(unique));
		} else {
			onToggle([]);
		}
	};

	// No ellipsis/clip: the tree panel scrolls horizontally, so labels render in full
	// and their real width contributes to the content-sized (max-content) row.
	const labelPrimaryStyles = {
		whiteSpace: "nowrap",
	};

	const levelIndent = 5;

	const renderTreeRow = ({ row, style }) => {
		if (!row) return null;

		const node = row.node || {};
		const depth = Math.max(row.depth || 0, 0);
		const hasChildren = (node.children || []).length > 0;
		const isExpanded = expandedLookup.has(node.nodeId);
		const nodeProductIds = getNodeProductIds(node);
		const nodeHasProducts = nodeProductIds.length > 0;
		const tooltipText = [node.productNumber, node.productName].filter(Boolean).join(" | ");
		const labelPrimary = node.productNumber || node.productName || "";
		const labelSecondary = node.productName;
		const canCheckNode = showCheckboxes && nodeHasProducts;
		const isChecked = canCheckNode && nodeProductIds.every((id) => checkedLookup.has(id));
		const isIndeterminate =
			canCheckNode && !isChecked && nodeProductIds.some((id) => checkedLookup.has(id));
		const isSelected = selectedNodeId === node.nodeId;
		const isFilterMatch = filtersActive && node.productId && highlightSet.has(node.productId);
		const isBomSearchMatch = bomSearchMatchSet.has(node.nodeId);
		const isActiveBomMatch = node.nodeId === activeMatchNodeId;
		const modelInfo = getModelInfoForNode(node) || {};
		const nodeHas3D = !!modelInfo.has3D;
		const nodeHas2D = !!modelInfo.has2D;
		const hasModelBadges = nodeHas3D || nodeHas2D;
		let backgroundColor = "transparent";

		if (isSelected) {
			backgroundColor = alpha(theme.palette.primary.main, 0.18);
		} else if (isFilterMatch) {
			backgroundColor = alpha(
				theme.palette.secondary.main || theme.palette.primary.main,
				0.12
			);
		} else if (isActiveBomMatch) {
			backgroundColor = "#fff59d";
		} else if (isBomSearchMatch) {
			backgroundColor = "#fffde7";
		}

		return (
			<Box
				key={node.nodeId}
				style={style}
				sx={{
					display: "flex",
					alignItems: "center",
					width: "100%",
					boxSizing: "border-box",
					px: 0.5,
					py: 0.35,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						columnGap: 0.35,
						width: "100%",
						paddingLeft: (depth + 1) * levelIndent,
					}}
				>
					<Box
						sx={{
							width: 28,
							height: 28,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						{hasChildren ? (
							<IconButton
								size="small"
								sx={{ width: 28, height: 28 }}
								onClick={(event) => {
									event.stopPropagation();
									handleToggle(node.nodeId);
								}}
							>
								{isExpanded ? (
									<ExpandMoreIcon fontSize="small" />
								) : (
									<ChevronRightIcon fontSize="small" />
								)}
							</IconButton>
						) : (
							<Box sx={{ width: 32, height: 32 }} />
						)}
					</Box>
					{showCheckboxes ? (
						<Checkbox
							size="small"
							sx={{ p: 0.35, mr: 0.35 }}
							disabled={!nodeHasProducts}
							checked={nodeHasProducts ? isChecked : false}
							indeterminate={nodeHasProducts ? isIndeterminate : false}
							onChange={() => handleNodeCheckboxToggle(node)}
						/>
					) : (
						<Box sx={{ width: 26 }} />
					)}
					<Tooltip title={tooltipText || ""} followCursor>
						<Box
							onClick={() => handleSelect(node.nodeId, node)}
							ref={(element) => registerNodeRef(node.nodeId, element)}
							sx={{
								cursor: "pointer",
								borderRadius: "6px",
								padding: "3px 6px",
								flexGrow: 1,
								minWidth: 0,
								display: "flex",
								flexDirection: "column",
								gap: 0.25,
								backgroundColor,
							}}
						>
							<Typography
								variant="body2"
								component="div"
								sx={{ fontWeight: isSelected ? 600 : 500, ...labelPrimaryStyles }}
							>
								{renderHighlightedText(
									labelPrimary,
									highlightTerm,
									theme.palette.primary.main
								)}
							</Typography>
							{isModalVariant && labelSecondary ? (
								<Typography variant="caption" color="text.secondary" sx={labelPrimaryStyles}>
									{renderHighlightedText(
										labelSecondary,
										highlightTerm,
										theme.palette.primary.main
									)}
								</Typography>
							) : null}
							<ReplacementPartNotice product={node} sx={labelPrimaryStyles} />
						</Box>
					</Tooltip>
					{node.productId ? (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.35, flexShrink: 0 }}>
							{nodeHas3D ? (
								<Tooltip title={t("UI_TEXT.TOOLTIP_OPEN_3D_MODEL")}>
									<IconButton
										size="small"
										sx={{ p: 0.25 }}
										onClick={(event) => handleModelIconButtonClick(event, node, "3d")}
									>
										<ModelIcon fontSize="inherit" sx={{ fontSize: 17 }} />
									</IconButton>
								</Tooltip>
							) : null}
							{nodeHas2D ? (
								<Tooltip title={t("UI_TEXT.TOOLTIP_OPEN_2D_DRAWING")}>
									<IconButton
										size="small"
										sx={{ p: 0.25 }}
										onClick={(event) => handleModelIconButtonClick(event, node, "2d")}
									>
										<ModelPDFIcon fontSize="inherit" sx={{ fontSize: 16 }} />
									</IconButton>
								</Tooltip>
							) : null}
							{isModalVariant ? (
								<Tooltip title={tooltipText || ""}>
									<IconButton
										size="small"
										onClick={(event) => {
											event.stopPropagation();
											if (onNavigateToProduct) {
												onNavigateToProduct(node.productId);
											}
										}}
									>
										<OpenInNewIcon fontSize="inherit" />
									</IconButton>
								</Tooltip>
							) : null}
						</Box>
					) : null}
				</Box>
			</Box>
		);
	};

	const renderMachineRow = () => {
		const hasChildren = normalizedTreeData.length > 0;
		const isExpanded = expandedLookup.has(machineNodeId);
		const machineChecked =
			machineProducts.length > 0 && machineProducts.every((id) => checkedLookup.has(id));
		const machineIndeterminate =
			machineProducts.length > 0 &&
			!machineChecked &&
			machineProducts.some((id) => checkedLookup.has(id));
		const machineIsSelected = selectedNodeId === machineNodeId;
		const machineBackgroundColor = machineIsSelected
			? alpha(theme.palette.primary.main, 0.18)
			: "transparent";

		return (
			<Box
				ref={machineRowRef}
				sx={{
					display: "flex",
					alignItems: "center",
					width: "100%",
					boxSizing: "border-box",
					px: 0.75,
					py: 0.75,
					columnGap: 0.5,
					borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
				}}
			>
				<Box
					sx={{
						width: 32,
						height: 32,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{hasChildren ? (
						<IconButton
							size="small"
							sx={{ width: 32, height: 32 }}
							onClick={(event) => {
								event.stopPropagation();
								handleToggle(machineNodeId);
							}}
						>
							{isExpanded ? (
								<ExpandMoreIcon fontSize="small" />
							) : (
								<ChevronRightIcon fontSize="small" />
							)}
						</IconButton>
					) : (
						<Box sx={{ width: 24, height: 24 }} />
					)}
				</Box>
				{showCheckboxes ? (
					<Checkbox
						size="small"
						sx={{ p: 0.5, mr: 0.25 }}
						disabled={machineProducts.length === 0}
						checked={machineProducts.length === 0 ? false : machineChecked}
						indeterminate={
							machineProducts.length === 0 ? false : machineIndeterminate
						}
						onChange={handleMachineCheckboxToggle}
					/>
				) : (
					<Box sx={{ width: 26 }} />
				)}
				<Box
					onClick={() => handleSelect(machineNodeId)}
					sx={{
						cursor: "pointer",
						borderRadius: "6px",
						padding: "4px 8px",
						flexGrow: 1,
						display: "flex",
						flexDirection: "column",
						gap: 0.25,
						minWidth: 0,
						backgroundColor: machineBackgroundColor,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							fontWeight: 600,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{machineName}
					</Typography>
					{machineNumber ? (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
						>
							<b>{t('UI_TEXT.MACHINE')}:</b> {machineNumber}
						</Typography>
					) : null}
				</Box>
				{machineHasDocumentation && typeof onMachineDocumentationClick === "function" ? (
					<Tooltip title={t("TITLES.VIEW_DOCUMENTATION")}>
						<IconButton
							size="small"
							sx={{ p: 0.25, mr: 0.25 }}
							onClick={(event) => {
								event.stopPropagation();
								onMachineDocumentationClick(event);
							}}
						>
							<DescriptionOutlinedIcon
								fontSize="inherit"
								sx={{ fontSize: 17, color: theme.palette.text.secondary }}
							/>
						</IconButton>
					</Tooltip>
				) : null}
			</Box>
		);
	};

	const renderControlsAndTree = (showHelperText = true) => {
		const disableTreeActions = isLoading || normalizedTreeData.length === 0;
		const normalizedPlaceholder = bomSearchPlaceholder || "Search BOM";
		const skeletonRows = Array.from({ length: 6 });
		const listHeight = typeof maxHeight === "number" ? maxHeight : 360;
		const rowHeight = isModalVariant ? 46 : 38;
		const machineRowHeight = rowHeight + 18;
		const gapHeight = 10;
		const listViewportHeight = Math.max(listHeight - machineRowHeight - gapHeight, rowHeight * 3);
		const showNoData = !isLoading && normalizedTreeData.length === 0;
		const hasVisibleNodes = visibleTreeRows.length > 0;
		const rootExpanded = expandedLookup.has(machineNodeId);

		return (
			<Box sx={{ display: "flex", flexDirection: "column", rowGap: 1, p: 1 }}>
				{showHelperText ? (
					<Typography variant="body2" color="text.secondary">
						{helperText}
					</Typography>
				) : null}
				<TextField
					fullWidth
					size="small"
					variant="outlined"
					value={bomSearchTerm}
					disabled={isLoading}
					onChange={(event) => setBomSearchTerm(event.target.value)}
					onKeyDown={handleBomSearchKeyDown}
					placeholder={normalizedPlaceholder}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<FiSearch size={16} />
							</InputAdornment>
						),
						endAdornment: bomSearchTerm ? (
							<InputAdornment position="end">
								<IconButton
									size="small"
									onClick={handleBomSearchClear}
									aria-label={t('UI_TEXT.CLEAR_BOM_SEARCH')}
								>
									<MdOutlineClear size={18} />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
				/>
				<Box
					sx={{
						display: "flex",
						flexWrap: "nowrap",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 0.5,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{bomSearchTerm ? (
							<>
								<Typography variant="caption" color="text.secondary">
									{`Matches: ${matchDisplay}`}
								</Typography>
								<IconButton
									size="small"
									onClick={focusPreviousMatch}
									disabled={matchCount === 0 || disableTreeActions}
								>
									<ChevronLeftIcon fontSize="inherit" />
								</IconButton>
								<IconButton
									size="small"
									onClick={focusNextMatch}
									disabled={matchCount === 0 || disableTreeActions}
								>
									<ChevronRightIcon fontSize="inherit" />
								</IconButton>
							</>
						) : (
							<Typography variant="caption" color="text.secondary">
								{filtersActive ? t('UI_TEXT.FILTERS_ACTIVE') : ""}
							</Typography>
						)}
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
						<Tooltip title={finalExpandAllLabel}>
							<span>
								<IconButton
									size="small"
									onClick={() => handleExpandCollapse(true)}
									disabled={disableTreeActions}
								>
									<FiChevronsDown size={18} />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title={finalCollapseAllLabel}>
							<span>
								<IconButton
									size="small"
									onClick={() => handleExpandCollapse(false)}
									disabled={disableTreeActions}
								>
									<FiChevronsUp size={18} />
								</IconButton>
							</span>
						</Tooltip>
						{/* <Tooltip title={clearSelectionLabel}>
							<span>
								<IconButton
									size="small"
									onClick={() => onClearSelection && onClearSelection()}
									disabled={!selectedNodeId}
								>
									<MdOutlineClear size={18} />
								</IconButton>
							</span>
						</Tooltip> */}
					</Box>
				</Box>
				<Box
					sx={{
						height: listHeight,
						maxHeight: listHeight,
						// Scroll BOTH ways: vertical for long trees, horizontal for deep/long rows.
						overflow: "auto",
						border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
						borderRadius: "6px",
					}}
				>
					{isLoading ? (
						<Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
							{skeletonRows.map((_, index) => (
								<Skeleton key={index} variant="rounded" height={rowHeight} />
							))}
						</Box>
					) : (
						// minWidth: max-content -> the inner grows to the widest row so nothing
						// clips; the container above scrolls horizontally to reveal it.
						<Box sx={{ minWidth: "max-content", display: "flex", flexDirection: "column", pr: 1 }}>
							{renderMachineRow()}
							<Box sx={{ height: 8, flexShrink: 0 }} />
							{hasVisibleNodes && rootExpanded ? (
								visibleTreeRows.map((row) =>
									renderTreeRow({ row, style: undefined })
								)
							) : (
								<Box
									sx={{
										flexGrow: 1,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										px: 2,
										pb: 2,
										textAlign: "center",
									}}
								>
									<Typography variant="body2" color="text.secondary">
										{showNoData
											? noDataLabel
											: rootExpanded
												? t('UI_TEXT.NO_MATCHING_PARTS_ADJUST_FILTERS_OR_SEARCH')
												: t('UI_TEXT.EXPAND_THE_MACHINE_TO_VIEW_BOM_PARTS')}
									</Typography>
								</Box>
							)}
						</Box>
					)}
				</Box>
			</Box>
		);
	};

	if (isModalVariant) {
		return renderControlsAndTree(false);
	}

	return (
		<Accordion
			defaultExpanded
			sx={{
				marginTop: "10px",
				borderRadius: "5px",
				boxShadow: "none",
				border: "1px solid #ddd",
			}}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls="bom-tree-content"
				id="bom-tree-header"
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						width: "100%",
						justifyContent: "space-between",
						gap: 1,
					}}
				>
					<Typography sx={{ fontSize: "13px", fontWeight: "bold" }}>{title}</Typography>
					{onOpenModal ? (
						<Tooltip title={modalButtonLabel}>
							<span>
								<IconButton
									size="small"
									onClick={(event) => {
										event.stopPropagation();
										onOpenModal();
									}}
									disabled={modalOpening}
								>
									{modalOpening ? (
										<CircularProgress size={18} />
									) : (
											<OpenInFullIcon fontSize="small" />
									)}
								</IconButton>
							</span>
						</Tooltip>
					) : null}
				</Box>
			</AccordionSummary>
			<AccordionDetails>{renderControlsAndTree(false)}</AccordionDetails>
		</Accordion>
	);
};

export default BomViewer;
